import React, { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  LinearProgress,
  Avatar,
  AvatarGroup,
  Tooltip,
  Button,
  IconButton,
  Menu,
  MenuItem,
  alpha,
} from '@mui/material';
import FlagIcon from '@mui/icons-material/Flag';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import InsightsIcon from '@mui/icons-material/Insights';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import dayjs from 'dayjs';
import { Sprint, getSprintHealth } from '../../entities/sprint/model/types';
import { Task, TaskStatus } from '../../types/common';
import { BoardMember } from '../../hooks/useBoardMembers';
import CountdownTimer from './CountdownTimer';

interface SprintDashboardCardProps {
  sprint: Sprint;
  tasks: Task[];
  boardMembers: BoardMember[];
  actionLoading?: boolean;
  canManageSprint?: boolean;
  onStart: () => void;
  onComplete: () => void;
  onViewBoard: () => void;
  onViewInsights: () => void;
}

const STATUS_META: Record<Sprint['status'], { label: string; color: 'default' | 'success' | 'primary' }> = {
  NOT_STARTED: { label: 'Planning', color: 'default' },
  ACTIVE: { label: 'Active', color: 'success' },
  COMPLETED: { label: 'Completed', color: 'primary' },
};

const HEALTH_META: Record<'on-track' | 'at-risk' | 'delayed', { label: string; color: string }> = {
  'on-track': { label: 'On Track', color: '#2e7d32' },
  'at-risk': { label: 'At Risk', color: '#f9a825' },
  delayed: { label: 'Delayed', color: '#e53935' },
};

const SprintDashboardCard: React.FC<SprintDashboardCardProps> = ({
  sprint,
  tasks,
  boardMembers,
  actionLoading,
  canManageSprint = true,
  onStart,
  onComplete,
  onViewBoard,
  onViewInsights,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const sprintTasks = useMemo(
    () => tasks.filter((t) => t.sprintId === sprint.id),
    [tasks, sprint.id]
  );

  const totalTasks = sprintTasks.length;
  const completedTasks = sprintTasks.filter((t) => t.status === TaskStatus.DONE).length;
  const totalPoints = sprintTasks.reduce((sum, t) => sum + (t.storyPoints || 0), 0);
  const completedPoints = sprintTasks
    .filter((t) => t.status === TaskStatus.DONE)
    .reduce((sum, t) => sum + (t.storyPoints || 0), 0);

  const progress = totalTasks === 0 ? 0 : Math.round((completedTasks / totalTasks) * 100);
  const health = getSprintHealth(sprint, progress);

  const assigneeIds = useMemo(
    () => Array.from(new Set(sprintTasks.map((t) => t.assigneeId).filter(Boolean))) as string[],
    [sprintTasks]
  );
  const teamMembers = boardMembers.filter((m) => assigneeIds.includes(String(m.id)));

  const statusMeta = STATUS_META[sprint.status] || STATUS_META.NOT_STARTED;
  const progressColor = progress >= 75 ? '#2e7d32' : progress >= 50 ? '#4A90E2' : '#f9a825';

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        background:
          sprint.status === 'ACTIVE'
            ? 'linear-gradient(135deg, rgba(74,144,226,0.05) 0%, rgba(90,185,234,0.02) 100%)'
            : 'background.paper',
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        '&:hover': {
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <FlagIcon color="primary" fontSize="small" />
          <Typography variant="h6" fontWeight={700} noWrap title={sprint.name}>
            {sprint.name}
          </Typography>
          <Chip label={statusMeta.label} color={statusMeta.color} size="small" />
          {health && (
            <Chip
              label={HEALTH_META[health].label}
              size="small"
              sx={{
                bgcolor: alpha(HEALTH_META[health].color, 0.12),
                color: HEALTH_META[health].color,
                fontWeight: 700,
              }}
            />
          )}
        </Box>
        <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onViewBoard();
            }}
          >
            <ViewKanbanIcon fontSize="small" sx={{ mr: 1 }} /> View Sprint Board
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onViewInsights();
            }}
          >
            <InsightsIcon fontSize="small" sx={{ mr: 1 }} /> View Sprint Report
          </MenuItem>
        </Menu>
      </Box>

      {sprint.goal && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {sprint.goal}
        </Typography>
      )}

      {/* Dates + countdown */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CalendarTodayIcon sx={{ fontSize: 14 }} color="action" />
          <Typography variant="caption" color="text.secondary">
            {dayjs(sprint.startDate).format('MMM D')} – {dayjs(sprint.endDate).format('MMM D, YYYY')}
          </Typography>
        </Box>
        {sprint.status === 'ACTIVE' && <CountdownTimer targetDate={sprint.endDate} compact />}
        {sprint.status === 'NOT_STARTED' && (
          <Typography variant="caption" color="text.secondary">
            Starts {dayjs(sprint.startDate).format('MMM D, YYYY')}
          </Typography>
        )}
        {sprint.status === 'COMPLETED' && sprint.completedAt && (
          <Typography variant="caption" color="text.secondary">
            Completed {dayjs(sprint.completedAt).format('MMM D, YYYY')}
          </Typography>
        )}
      </Box>

      {/* Progress */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {completedTasks}/{totalTasks} tasks · {completedPoints}/{totalPoints} pts
          </Typography>
          <Typography variant="caption" fontWeight={700} sx={{ color: progressColor }}>
            {progress}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: alpha(progressColor, 0.15),
            '& .MuiLinearProgress-bar': { bgcolor: progressColor, borderRadius: 4 },
          }}
        />
      </Box>

      {/* Footer: avatars + actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {teamMembers.length > 0 ? (
          <AvatarGroup max={5} sx={{ '& .MuiAvatar-root': { width: 28, height: 28, fontSize: 12 } }}>
            {teamMembers.map((m) => (
              <Tooltip key={m.id} title={m.fullName || m.userName}>
                <Avatar>{(m.fullName || m.userName || '?').charAt(0).toUpperCase()}</Avatar>
              </Tooltip>
            ))}
          </AvatarGroup>
        ) : (
          <Typography variant="caption" color="text.secondary">
            No members assigned yet
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1 }}>
          {sprint.status === 'NOT_STARTED' && canManageSprint && (
            <Button
              size="small"
              variant="contained"
              startIcon={<PlayArrowIcon />}
              onClick={onStart}
              disabled={actionLoading}
            >
              Start
            </Button>
          )}
          {sprint.status === 'ACTIVE' && canManageSprint && (
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={onComplete}
              disabled={actionLoading}
            >
              Complete
            </Button>
          )}
          <Button size="small" variant="outlined" onClick={onViewBoard}>
            View Board
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default SprintDashboardCard;
